import React, { useEffect, useState } from 'react';

// /social/media-proxy requires a Bearer JWT (it's a same-origin CORS-bypass
// helper, not a public proxy — leaving it open would make this backend an
// open SSRF-adjacent relay for arbitrary http(s) URLs). A plain <img src>
// can't attach that header, so the proxy fallback 401s on every image that
// needs it (found live: ~50 repeated 401s per search for Instagram CDN
// avatars whose CORS/referrer policy blocks direct hotlinking). Fetch the
// proxy URL with fetch() (which does attach Authorization) and hand the
// <img> a blob: URL instead.

const TOKEN_KEYS = ['auth_token', 'token', 'access_token'];
function getToken(): string | null {
  for (const k of TOKEN_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

interface AuthedImageProps {
  src: string;        // try this directly first — many URLs are hotlinkable as-is
  proxySrc: string;   // authenticated fallback via /social/media-proxy
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
}

// Videos in this module always went straight through the proxy (no
// try-direct-first step, unlike images) — same 401 problem, just
// unconditional instead of fallback-triggered. Fetch-and-blob unconditionally.
export function useAuthedBlobUrl(proxySrc: string | null): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!proxySrc) { setBlobUrl(null); return; }
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(proxySrc, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error(`proxy fetch failed: ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [proxySrc]);

  return blobUrl;
}

export const AuthedImage: React.FC<AuthedImageProps> = ({ src, proxySrc, alt = '', className, style }) => {
  const [displaySrc, setDisplaySrc] = useState(src);
  const [failed, setFailed] = useState(false);
  const [triedProxy, setTriedProxy] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const handleError = async () => {
    if (triedProxy) { setFailed(true); return; }
    setTriedProxy(true);
    try {
      const token = getToken();
      const res = await fetch(proxySrc, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`proxy fetch failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setDisplaySrc(url);
    } catch {
      setFailed(true);
    }
  };

  if (failed) {
    return (
      <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
        <span style={{ color: '#374151', fontSize: 10, fontFamily: 'monospace' }}>Image unavailable</span>
      </div>
    );
  }

  return <img src={displaySrc} alt={alt} className={className} style={style} onError={handleError} />;
};

export default AuthedImage;
